import { defineStore } from 'pinia'
import { ref, computed, nextTick, watch } from 'vue'
import { v4 as generateUuid } from 'uuid'
import omit from 'lodash/omit'
import omitBy from 'lodash/omitBy'

import SFetch from '@app/utils/sfetch'
import api from '../api'
import serialize from '../utils/serialize'
import {
  currentLocale,
  localisedKeyFor,
  LOCALISED_FIELDS,
  FLAT_LOCALISED_ATTRIBUTE,
} from '../utils/localised-segment'
import { useTriggersStore } from './triggers'
import { useFiltersStore } from './filters'
import { segmentVisibilities } from '../types'
import type { Segment, SegmentDraft, SegmentErrorResponse } from '../types'
import { useCompaniesStore } from './companies'
import { useInsightsRootStore } from './insights-root'

interface SegmentApiResponse extends Segment {
  segment_filters: unknown[]
}

interface LoadSegmentPayload {
  id: number
  partner_segment_id?: number | null
  last_request_ref?: string | null
}

const defaultVisibility = segmentVisibilities.PRIVATE_VISIBLE
const defaultSegment = (): SegmentDraft => ({
  id: null,
  name: '',
  recommended: false,
  visibility: defaultVisibility,
  permissions: {},
  partner: '',
  insight_triggers_count: 0,
})

function syncCurrentLocale(seg: Partial<Segment>): void {
  const locale = currentLocale()
  LOCALISED_FIELDS.forEach((field) => {
    const key = localisedKeyFor(field)
    if (seg[key]) {
      seg[key] = { ...seg[key], [locale]: seg[field] ?? '' }
    }
  })
}

function applyPeriodFromResponse(
  rootStore: ReturnType<typeof useInsightsRootStore>,
  resp: Segment
): void {
  rootStore.setLedgerSelectDate((resp.period_date as string) ?? null)
  rootStore.setLedgerSelectLockType((resp.period_lock_type as string) ?? null)
  rootStore.setPeriodSelection((resp.period_type as string) ?? null)
}

export const useSegmentsStore = defineStore('insights-segments', () => {
  const filtersStore = useFiltersStore()
  const rootStore = useInsightsRootStore()
  const companiesStore = useCompaniesStore()
  const triggersStore = useTriggersStore()

  const segmentModalVisible = ref(false)
  const segments = ref<Segment[]>([])
  const segment = ref<Segment | null>(null)
  const isSegmentLoading = ref(false)
  const editedSegment = ref<SegmentDraft>(defaultSegment())
  const segmentModalType = ref<string | null>(null)

  // A saved query ends the "New query" state, and it must end here rather than
  // at the save: deleting that query later unselects it, and the page would
  // return to the stale "New query" name.
  watch(segment, (current) => {
    if (current?.id) rootStore.setNewQuery(false)
  })

  const isSegmentModified = computed((): boolean => {
    if (isSegmentLoading.value) return false
    if (!segment.value || !segment.value.id) return false
    if (filtersStore.isActiveFiltersModified) return true

    if (rootStore.ledgerSelectDate !== (segment.value.period_date || null))
      return true
    if (
      (rootStore.ledgerSelectLockType || null) !==
      (segment.value.period_lock_type || null)
    )
      return true
    if (rootStore.periodSelection !== segment.value.period_type) return true
    return false
  })

  const lastSegmentRef = computed((): string | null | undefined => {
    if (isSegmentModified.value) return null
    return segment.value?.last_request_ref ?? null
  })

  function setSegments(newSegments: Segment[]): void {
    segments.value = newSegments
  }

  function setSegment(newSegment: Segment | null): void {
    segment.value = newSegment
  }

  function updateSegmentInList(newSegment: Segment): void {
    const index = segments.value.findIndex((s) => s.id === newSegment.id)
    if (index !== -1) {
      segments.value[index] = newSegment
    }
  }

  function removeSegmentFromList(segmentId: number): void {
    segments.value = segments.value.filter((s) => s.id !== segmentId)
  }

  function setEditedSegmentFromSegment(seg: Segment | null): void {
    const newSegment: SegmentDraft = {
      ...defaultSegment(),
      ...omit(seg || {}, ['created_at', 'last_visited_at']),
    }
    if (!newSegment.visibility) newSegment.visibility = defaultVisibility
    if (!newSegment.permissions) newSegment.permissions = {}
    editedSegment.value = newSegment
  }

  function openSaveSegmentModal(): void {
    setEditedSegmentFromSegment(segment.value)
    segmentModalType.value = 'save'
  }

  function openSaveAsSegmentModal(): void {
    setEditedSegmentFromSegment({
      ...segment.value,
      visibility: defaultVisibility,
    } as Segment)
    segmentModalType.value = 'save_as'
  }

  function openEditSegmentModal(seg: Segment): void {
    setEditedSegmentFromSegment(seg)
    segmentModalType.value = 'edit'
  }

  function closeEditSegmentModal(): void {
    editedSegment.value = defaultSegment()
    segmentModalType.value = null
    segmentModalVisible.value = false
  }

  function unselectSegment(): void {
    segment.value = null
    rootStore.setNewQuery(false)
    rootStore.setLedgerSelectDate(null)
    rootStore.setLedgerSelectLockType(null)
    rootStore.setPeriodSelection(null)
    filtersStore.unsetActiveFilters()
  }

  function removeSegmentById(segmentId: number): void {
    removeSegmentFromList(segmentId)
  }

  function setEditedSegment(seg: SegmentDraft): void {
    editedSegment.value = seg
  }

  function showSegmentModal(): void {
    segmentModalVisible.value = true
  }

  function hideSegmentModal(): void {
    segmentModalVisible.value = false
  }

  function addToRecent(seg: Segment): void {
    rootStore.addRecentSegment(seg.id)
  }

  async function fetchSegments(): Promise<void> {
    const fetchedSegments = (await api.fetchSegments()) as Segment[]
    segments.value = fetchedSegments
  }

  // Returns whether the segment actually loaded. fetchSegment reports every
  // request failure (403, 404, network) as an empty response, so a false
  // return is the only failure signal callers get. By default an empty
  // response bounces the user to the companies overview; pass
  // redirectOnEmpty: false to stay on the page and handle the failure locally.
  async function loadSegment(
    payload: LoadSegmentPayload,
    { redirectOnEmpty = true }: { redirectOnEmpty?: boolean } = {}
  ): Promise<boolean> {
    segment.value = payload as unknown as Segment
    isSegmentLoading.value = true
    companiesStore.initCompaniesRequest(
      payload.last_request_ref || generateUuid()
    )

    const firmId = window.Silverfin.currentFirmId
    const rawResponse = (await api.fetchSegment({
      segmentId: payload.id,
      partnerSegmentId: payload.partner_segment_id,
    })) as SegmentApiResponse | Record<string, never>

    // Guard: another segment may have become active (another loadSegment or a
    // createSegment/save-as-new) while our fetch was in flight. If so, drop
    // the stale response — overwriting segment.value would move the current-
    // segment highlight back to the older target.
    if (segment.value?.id !== payload.id) return false

    if (rawResponse && Object.keys(rawResponse).length > 0) {
      const { segment_filters, ...loadedSegment } =
        rawResponse as SegmentApiResponse
      segment.value = loadedSegment
      isSegmentLoading.value = false
      filtersStore.setActiveFilters(segment_filters)
      applyPeriodFromResponse(rootStore, loadedSegment)
      // Surface firm segments in the "Recently used" list even when they have
      // no SegmentRun yet (e.g. opened from the queries overview). Partner
      // segments are shown under "Recommended" instead. Skip if it's already
      // in the list so clicking around inside Insights doesn't reorder items.
      if (
        !loadedSegment.partner_segment_id &&
        loadedSegment.id &&
        !rootStore.recentSegments.includes(loadedSegment.id)
      ) {
        rootStore.addRecentSegment(loadedSegment.id)
      }
      // Let the activeFilters watcher flush so isSegmentModified settles before fetchTriggers lands.
      await nextTick()
      void triggersStore.fetchTriggers(loadedSegment.id ?? undefined)
      return true
    }

    if (redirectOnEmpty) {
      window.location.href =
        window.Silverfin.Routes.companies_overview_index_path({
          firm_id: firmId,
          filters: '[]',
        })
    } else {
      // Undo the optimistic stub set above — the guard ensured no newer load
      // replaced it, so nulling cannot clobber another segment.
      segment.value = null
      isSegmentLoading.value = false
    }
    return false
  }

  async function silentReloadCurrentSegment(): Promise<void> {
    const currentSegment = segment.value
    if (!currentSegment?.id) return
    if (isSegmentModified.value) return

    const response = (await api.fetchSegment({
      segmentId: currentSegment.id,
      partnerSegmentId: currentSegment.partner_segment_id,
    })) as SegmentApiResponse | Record<string, never>

    if (Object.keys(response).length === 0) return

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { segment_filters, ...refreshedSegment } =
      response as SegmentApiResponse
    updateSegmentInList(refreshedSegment)
    if (segment.value?.id === currentSegment.id) {
      segment.value = refreshedSegment
    }
  }

  async function createSegment(): Promise<void> {
    const { id, ...seg } = editedSegment.value
    syncCurrentLocale(seg)
    const firmId = window.Silverfin.currentFirmId
    const segmentsEndpoint = window.Silverfin.Routes.segments_path(firmId)

    const body = {
      // Partner (recommended) segments carry their display name as flat
      // name_<locale> attributes; firm segments carry names via localised_names,
      // so drop the flat ones rather than send both representations.
      segment: {
        ...omitBy(seg, (_value, key) => FLAT_LOCALISED_ATTRIBUTE.test(key)),
        filters: serialize.filterSettings(
          filtersStore.getActiveFilters,
          filtersStore.allFiltersByType,
          { includeId: !id }
        ) as unknown,
        period_date: rootStore.ledgerSelectDate,
        period_lock_type: rootStore.ledgerSelectLockType,
        period_type: rootStore.periodSelection,
      },
    }

    const { segment_filters, ...responseSegment } =
      (await SFetch.json<SegmentApiResponse>(
        segmentsEndpoint,
        { method: 'POST', body },
        { camelizeJSON: false }
      )) as SegmentApiResponse

    closeEditSegmentModal()
    segment.value = responseSegment
    rootStore.addRecentSegment(responseSegment.id)
    filtersStore.setActiveFilters(segment_filters)
    await fetchSegments()
    // Let the activeFilters watcher flush so isSegmentModified settles before fetchTriggers lands.
    await nextTick()
    void triggersStore.fetchTriggers(responseSegment.id ?? undefined)
  }

  async function saveAsNewSegment({
    name,
    visibility,
  }: {
    name: string
    visibility: string
  }): Promise<void> {
    if (editedSegment.value.id !== segment.value?.id) {
      await loadSegment(editedSegment.value as unknown as LoadSegmentPayload)
    }
    const seg = {
      ...omit(segment.value, 'created_at', 'last_visited_at'),
      visibility,
      name,
      localised_names: { [currentLocale()]: name },
    }
    editedSegment.value = seg as Segment
    await createSegment()
  }

  async function updateSegmentName(name: string): Promise<void> {
    const { id } = segment.value as Segment
    const firmId = window.Silverfin.currentFirmId
    const segmentEndpoint = window.Silverfin.Routes.segment_path(firmId, id)

    try {
      const body = { segment: { name } }
      const response = (await SFetch.json<SegmentApiResponse>(
        segmentEndpoint,
        { method: 'PATCH', body },
        { camelizeJSON: false }
      )) as SegmentApiResponse

      // The request sends the name alone, so the returned filters hold nothing
      // new. Assigning them would trigger the deep watcher on activeFilters in
      // insights-companies.vue and re-run the query.
      const updatedSegment = omit(response, ['segment_filters'])

      updateSegmentInList({ ...updatedSegment, name })
      segment.value = updatedSegment
      applyPeriodFromResponse(rootStore, updatedSegment)
    } catch (e) {
      window.SilverfinFlash.addFlash(
        'error',
        (e as SegmentErrorResponse).body.errorMessage
      )
    }
  }

  function updatePartnerSegment(partnerSegmentID: number): void {
    const editUrl =
      window.Silverfin.Routes.edit_partners_template_collection_segment_path({
        id: partnerSegmentID,
      })
    const segmentParams = JSON.stringify({
      filters: serialize.filterSettings(
        filtersStore.activeFilters,
        filtersStore.allFiltersByType
      ) as unknown,
      period_lock_type: rootStore.ledgerSelectLockType,
      period_date: rootStore.ledgerSelectDate,
      period_type: rootStore.periodSelection,
    })
    window.location.href = `${editUrl}?firm_segment=${encodeURIComponent(
      segmentParams
    )}`
  }

  async function updateSegment(
    updatedSegment: Segment | null = null
  ): Promise<void> {
    const source = (updatedSegment || segment.value) as Segment
    const { id } = source
    const seg = omit(source, ['id', 'created_at', 'last_visited_at'])
    syncCurrentLocale(seg)
    const firmId = window.Silverfin.currentFirmId
    const segmentEndpoint = window.Silverfin.Routes.segment_path(firmId, id)

    const payload: Record<string, unknown> = { ...seg }

    // The root store period selectors describe the *active* segment. Stamping
    // them while saving another segment (edit-details on the queries overview,
    // where the active segment may be unset or a copy source) would overwrite
    // that segment's stored period — invalidating its runs and triggers.
    if (id === segment.value?.id) {
      payload.period_lock_type = rootStore.ledgerSelectLockType
      payload.period_date = rootStore.ledgerSelectDate
      payload.period_type = rootStore.periodSelection
    }

    const filtersModified = filtersStore.isActiveFiltersModified

    if (filtersModified) {
      payload.filters = serialize.filterSettings(
        filtersStore.getActiveFilters,
        filtersStore.allFiltersByType
      )
    }

    const { segment_filters, ...responseSegment } =
      (await SFetch.json<SegmentApiResponse>(
        segmentEndpoint,
        { method: 'PUT', body: { segment: payload } },
        { camelizeJSON: false }
      )) as SegmentApiResponse

    if (id === segment.value?.id) {
      segment.value = responseSegment
      // setActiveFilters assigns a new array, and the deep watcher on
      // activeFilters in insights-companies.vue re-runs the query on any
      // assignment. A save that left the filters alone must not pay for a run.
      if (filtersModified) {
        filtersStore.setActiveFilters(segment_filters)
      }
      applyPeriodFromResponse(rootStore, responseSegment)
    }
    updateSegmentInList(responseSegment)
    closeEditSegmentModal()
  }

  async function deleteSegment({ id }: { id?: number } = {}): Promise<boolean> {
    const segmentId = id || (segment.value as Segment).id
    const firmId = window.Silverfin.currentFirmId
    const segmentEndpoint = window.Silverfin.Routes.segment_path(
      firmId,
      segmentId
    )

    try {
      await SFetch.json(
        segmentEndpoint,
        { method: 'DELETE' },
        { camelizeJSON: false }
      )
      segment.value = null
      removeSegmentFromList(segmentId)
      return true
    } catch (e) {
      window.SilverfinFlash.addFlash(
        'error',
        (e as SegmentErrorResponse).body.errorMessage
      )
      return false
    }
  }

  function setDefaultPeriod(): void {
    rootStore.setPeriodSelection('financial_year')
  }

  return {
    segmentModalVisible,
    segments,
    segment,
    isSegmentLoading,
    editedSegment,
    segmentModalType,
    isSegmentModified,
    lastSegmentRef,
    setSegments,
    setSegment,
    updateSegmentInList,
    removeSegmentFromList,
    openSaveSegmentModal,
    openSaveAsSegmentModal,
    openEditSegmentModal,
    closeEditSegmentModal,
    unselectSegment,
    removeSegmentById,
    setEditedSegment,
    showSegmentModal,
    hideSegmentModal,
    addToRecent,
    fetchSegments,
    loadSegment,
    setDefaultPeriod,
    silentReloadCurrentSegment,
    createSegment,
    saveAsNewSegment,
    updateSegmentName,
    updatePartnerSegment,
    updateSegment,
    deleteSegment,
  }
})
